import {
  createMePaymentMethodSetupIntentV1,
  deleteMePaymentMethodV1,
  getMePaymentMethodsV1,
  setMePaymentMethodPreferredV1,
  updateMePaymentMethodV1,
} from '../../api/me-—-payment-methods/me-—-payment-methods';
import { PaymentMethodDto, SetupIntentDto, UpdatePaymentMethodRequest } from '../../api/model';

/**
 * Fetches the authenticated user's saved cards, read live from Stripe.
 *
 * The preferred card is returned first by the API. An empty array means the user
 * has no saved cards yet — distinct from `undefined`, which signals a failure.
 *
 * Authentication is injected automatically by the underlying `customFetch` interceptor.
 *
 * @param language - BCP 47 locale tag forwarded as `Accept-Language`.
 * @returns `PaymentMethodDto[]` on HTTP 200.
 * @returns `undefined` on 401 (unauthenticated) or network error.
 */
export async function getPaymentMethods(language?: string): Promise<PaymentMethodDto[] | undefined> {
  try {
    const response = await getMePaymentMethodsV1({ headers: { 'Accept-Language': language ?? '' } });

    if (response.status === 200) {
      return response.data as PaymentMethodDto[];
    }

    console.warn(`getPaymentMethods: unexpected status ${response.status}`);
    return undefined;
  } catch (error) {
    console.warn('getPaymentMethods: request failed:', error);
    return undefined;
  }
}

/**
 * Creates a Stripe SetupIntent for adding a new card, creating the Stripe customer on first use.
 *
 * The returned `clientSecret` is handed to Stripe Elements, which sends the card data straight
 * to Stripe — card details never reach the Amorim API (PCI SAQ-A). The `publishableKey` is
 * delivered per request rather than via a build-time env var, so `loadStripe` must be called
 * with the value from this response.
 *
 * Authentication is injected automatically by the underlying `customFetch` interceptor.
 *
 * @returns `SetupIntentDto` on HTTP 200.
 * @returns `undefined` on 401 (unauthenticated) or network error.
 */
export async function createSetupIntent(): Promise<SetupIntentDto | undefined> {
  try {
    const response = await createMePaymentMethodSetupIntentV1();

    if (response.status === 200) {
      return response.data as SetupIntentDto;
    }

    console.warn(`createSetupIntent: unexpected status ${response.status}`);
    return undefined;
  } catch (error) {
    console.warn('createSetupIntent: request failed:', error);
    return undefined;
  }
}

/**
 * Partially updates a saved card — expiry month/year and/or cardholder name.
 *
 * Null fields are left untouched. The card number and CVC can never be updated (Stripe does
 * not allow it); changing the number means adding a new card.
 *
 * Authentication is injected automatically by the underlying `customFetch` interceptor.
 *
 * @param id - The Stripe payment method id to update.
 * @param data - The fields to change.
 * @param language - BCP 47 locale tag forwarded as `Accept-Language`.
 * @returns `true` on HTTP 204 (the endpoint returns no body).
 * @returns `false` on 400 (empty request or invalid expiry), 401, 404 (not the user's card), or network error.
 */
export async function updatePaymentMethod(
  id: string,
  data: UpdatePaymentMethodRequest,
  language?: string
): Promise<boolean> {
  try {
    const response = await updateMePaymentMethodV1(id, data, {
      headers: { 'Accept-Language': language ?? '' },
    });

    if (response.status === 204) {
      return true;
    }

    console.warn(`updatePaymentMethod: unexpected status ${response.status}`);
    return false;
  } catch (error) {
    console.warn('updatePaymentMethod: request failed:', error);
    return false;
  }
}

/**
 * Marks a saved card as the user's default (preferred) payment method in Stripe.
 *
 * Only one card can be preferred at a time — the API clears the flag from the others.
 *
 * Authentication is injected automatically by the underlying `customFetch` interceptor.
 *
 * @param id - The Stripe payment method id to mark as preferred.
 * @returns `true` on HTTP 204 (the endpoint returns no body).
 * @returns `false` on 401, 404 (not the user's card), or network error.
 */
export async function setPreferredPaymentMethod(id: string): Promise<boolean> {
  try {
    const response = await setMePaymentMethodPreferredV1(id);

    if (response.status === 204) {
      return true;
    }

    console.warn(`setPreferredPaymentMethod: unexpected status ${response.status}`);
    return false;
  } catch (error) {
    console.warn('setPreferredPaymentMethod: request failed:', error);
    return false;
  }
}

/**
 * Detaches a saved card from the user's Stripe customer.
 *
 * Authentication is injected automatically by the underlying `customFetch` interceptor.
 *
 * @param id - The Stripe payment method id to delete.
 * @returns `true` on HTTP 204 (the endpoint returns no body).
 * @returns `false` on 401, 404 (not the user's card), or network error.
 */
export async function deletePaymentMethod(id: string): Promise<boolean> {
  try {
    const response = await deleteMePaymentMethodV1(id);

    if (response.status === 204) {
      return true;
    }

    console.warn(`deletePaymentMethod: unexpected status ${response.status}`);
    return false;
  } catch (error) {
    console.warn('deletePaymentMethod: request failed:', error);
    return false;
  }
}
